import { msg } from "@lingui/core/macro";
import { createMemo, For, Show } from "solid-js";
import { Mail, Pencil, Plus, Trash2 } from "lucide-solid";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { NotFoundState } from "@/components/ui/not-found-state";
import { api, createApiMutation, createApiQuery } from "@/lib/api";
import type { components } from "@comandero/shared";
import { useI18n } from "@/providers/i18n-provider";
import { EmailTemplateEditor } from "./email-template-editor";

type EmailTemplate = components["schemas"]["EmailTemplate"];

interface EmailTemplateManagerProps {
  /**
   * Modo del editor, derivado de la URL (campaña #15b — addressable):
   *   - `"new"`  → `/admin/email/new`        (crear plantilla)
   *   - `"edit"` → `/admin/email/$templateId` (editar; `editId` es el id)
   *   - ausente  → `/admin/email`            (solo lista)
   */
  mode?: () => "new" | "edit" | undefined;
  /** Id de la plantilla a editar (cuando `mode() === "edit"`). */
  editId?: () => string | undefined;
  /** Cierra el editor navegando de vuelta a `/admin/email`. */
  onClose?: () => void;
  /** Abre el editor de creación navegando a `/admin/email/new`. */
  onNew?: () => void;
  /** Abre el editor de edición navegando a `/admin/email/$templateId`. */
  onEdit?: (id: string) => void;
}

/**
 * CRUD de plantillas de email (campaña #11). Lista las plantillas (sistema +
 * personalizadas), permite crear/editar/borrar. Las de sistema no son borrables.
 * El editor de creación/edición es addressable por URL (#15b): `/admin/email/new`
 * y `/admin/email/$templateId`.
 */
export function EmailTemplateManager(props: EmailTemplateManagerProps = {}) {
  const { t } = useI18n();

  const templatesQuery = createApiQuery<EmailTemplate[], components["schemas"]["ErrorModel"]>(
    () => ["admin", "email", "templates"],
    async () => api.GET("/api/admin/email/templates"),
    () => ({ staleTime: 30_000 }),
  );

  const deleteMutation = createApiMutation(async (vars: { id: string }) =>
    api.POST("/api/admin/email/templates/delete", { body: vars }),
  );

  // Estado del editor derivado de la URL (no de signals locales): copiar/pegar la
  // URL reabre el mismo editor en otro dispositivo.
  const isCreating = createMemo(() => props.mode?.() === "new");
  const isEditing = createMemo(() => props.mode?.() === "edit");
  const editing = createMemo<EmailTemplate | null>(() => {
    if (!isEditing()) {
      return null;
    }
    const id = props.editId?.();
    return templatesQuery.data?.find((tpl) => tpl.id === id) ?? null;
  });
  // La plantilla a editar no existe (id inválido o borrada) una vez cargada la lista.
  const editNotFound = createMemo(
    () => isEditing() && !templatesQuery.isLoading && editing() == null,
  );

  const handleDelete = async (tpl: EmailTemplate) => {
    if (tpl.isSystem) {
      return;
    }
    try {
      await deleteMutation.mutateAsync({ id: tpl.id });
      await templatesQuery.refetch();
    } catch (err) {
      console.error("Error deleting email template:", err);
    }
  };

  const onSaved = async () => {
    await templatesQuery.refetch();
    props.onClose?.();
  };

  return (
    <div class="space-y-8">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-3">
          <Mail class="h-8 w-8 text-primary" />
          <h2 class="text-3xl font-bold tracking-tight">{t(msg`Email Templates`)}</h2>
        </div>
        <Button class="gap-2" onClick={() => props.onNew?.()} disabled={isCreating()}>
          <Plus class="h-4 w-4" />
          {t(msg`New Template`)}
        </Button>
      </div>

      <Show when={editNotFound()}>
        <NotFoundState
          title={t(msg`Template not found`)}
          description={t(msg`This email template may have been deleted, or never existed.`)}
          primaryAction={{
            label: t(msg`Back to templates`),
            onClick: () => props.onClose?.(),
          }}
        />
      </Show>

      <Show when={isCreating() || editing()}>
        <EmailTemplateEditor
          template={editing()}
          onSaved={onSaved}
          onCancel={() => props.onClose?.()}
        />
      </Show>

      <Show
        when={!templatesQuery.isLoading}
        fallback={
          <div class="space-y-3">
            <For each={Array.from({ length: 4 }, () => 0)}>
              {() => <Skeleton class="h-20 w-full rounded-xl" />}
            </For>
          </div>
        }
      >
        <div class="space-y-3">
          <For each={templatesQuery.data ?? []}>
            {(tpl) => (
              <div class="flex items-center justify-between rounded-xl border border-border bg-card p-4">
                <div class="min-w-0">
                  <div class="flex items-center gap-2">
                    <span class="truncate font-semibold">{tpl.name}</span>
                    <Badge variant="secondary">{tpl.category}</Badge>
                    <Show when={tpl.isSystem}>
                      <Badge>{t(msg`System`)}</Badge>
                    </Show>
                    <Show when={!tpl.isActive}>
                      <Badge variant="outline">{t(msg`Inactive`)}</Badge>
                    </Show>
                  </div>
                  <p class="mt-1 truncate text-sm text-muted-foreground">{tpl.subject}</p>
                  <p class="mt-0.5 text-xs text-muted-foreground">
                    {(() => {
                      const { key } = tpl;
                      const version = tpl.currentVersion;
                      return t(msg`Key: ${key} · v${version}`);
                    })()}
                  </p>
                </div>
                <div class="flex shrink-0 items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    class="gap-1"
                    onClick={() => props.onEdit?.(tpl.id)}
                  >
                    <Pencil class="h-4 w-4" />
                    {t(msg`Edit`)}
                  </Button>
                  <Show when={!tpl.isSystem}>
                    <Button
                      variant="ghost"
                      size="sm"
                      class="gap-1 text-destructive"
                      disabled={deleteMutation.isPending}
                      onClick={() => handleDelete(tpl)}
                    >
                      <Trash2 class="h-4 w-4" />
                      {t(msg`Delete`)}
                    </Button>
                  </Show>
                </div>
              </div>
            )}
          </For>
        </div>
      </Show>
    </div>
  );
}
